"use client";

import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Check } from "lucide-react";

interface Options {
  content: ReactNode;
  confirmText: string;
  cancelText: string;
}

interface Props {
  confirm: (options: Options) => Promise<boolean | undefined>;
}

const ConfirmContext = createContext<Props>({
  confirm: () => Promise.resolve(undefined),
});

export default function ConfirmProvider({
  children,
}: {
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Options>();
  const confirmPromise = useRef<(confirm: boolean | undefined) => void>(null);

  const startConfirm = async (options: Options) => {
    setOptions(options);
    setOpen(true);
    return new Promise<boolean | undefined>((resolve) => {
      confirmPromise.current = resolve;
    });
  };

  const onConfirm = (confirm: boolean | undefined) => {
    confirmPromise.current?.(confirm);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      confirmPromise.current?.(undefined);
    }
  }, [open]);

  return (
    <ConfirmContext.Provider value={{ confirm: startConfirm }}>
      <Dialog
        header="Crop settings"
        visible={open}
        className="dialog"
        style={{ width: 500 }}
        headerStyle={{ padding: 16 }}
        contentClassName="border-t-1 border-gray-300"
        contentStyle={{ padding: 0 }}
        onHide={() => onConfirm(undefined)}
        footer={() => {
          return (
            <div className="flex justify-end gap-4 p-4 pt-0">
              <Button
                severity="secondary"
                style={{
                  height: 36,
                  margin: 0,
                  padding: "6px 10px",
                  gap: 4,
                }}
                label={options?.cancelText}
                icon={<Check size={18} />}
                onClick={() => onConfirm(false)}
              />
              <Button
                severity="info"
                style={{
                  height: 36,
                  margin: 0,
                  padding: "6px 10px",
                  gap: 4,
                }}
                label={options?.confirmText}
                icon={<Check size={18} />}
                onClick={() => onConfirm(true)}
              />
            </div>
          );
        }}
      >
        <div className="flex h-full overflow-auto p-4">{options?.content}</div>
      </Dialog>

      {children}
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
